import React, { useEffect, useState } from "react";
import { useField } from "formik";
import { ErrorMessage, Flex, HelpIndicator, Text, useDebouncedFn } from "@jsq/ds/core";
import { FormTextInput } from "@jsq/ds/forms";
import { Icon } from "@jsq/ds/icons";
import { PaymentsRoutingNumberType } from "#graphql/supergraphEnums";
import { useValidateRoutingNumberLazyQuery } from "#graphql/supergraphHooks";
import { clientContext } from "#utils/graphql_utils";
import { FieldGroup } from "../../../../../common/components/Form/Layout/FieldGroup";
import { MultiInputFormRow } from "../../../../../common/components/Form/Layout/MultiInputFormRow";
import { SimpleFormRow } from "../../../../../common/components/Form/Layout/SimpleFormRow";
import { useConsolidatedPaymentPrefContext } from "../../../Context";
import { useGenericBankValidation } from "../../../hooks/use-generic-bank-validation";
import { BankCountryMismatchWarning } from "../../BankCountryMismatchWarning";
import { type EftPaymentPrefInputType } from "../constants";
import { BankDetailsInputFields, EftBankDetailsInputSchema } from "./constants";
import { buildCaCpaRoutingNumber, normalizeRoutingNumber } from "./utils";

const BankCountryReadOnly: React.FC = () => {
  const { selectedBankCountry } = useConsolidatedPaymentPrefContext();
  return (
    <Flex flexDirection="column" gap={4}>
      <Text variant="secondary-sm">Bank country</Text>
      <Text variant="regular-md">{selectedBankCountry?.name}</Text>
    </Flex>
  );
};

export const BankDetails: React.FC = () => {
  const [showBankValidatorError, setShowBankValidatorError] = useState(false);
  const { selectedBankCountry } = useConsolidatedPaymentPrefContext();
  const [institutionNumberField] = useField<string>(BankDetailsInputFields.InstitutionNumber);
  const [transitNumberField] = useField<string>(BankDetailsInputFields.TransitNumber);

  const { handleClear, handleValidateCompleted } =
    useGenericBankValidation<EftPaymentPrefInputType>({
      nameField: BankDetailsInputFields.BankName,
      validatedCountryField: BankDetailsInputFields.BankCountryValidatedCountry,
    });

  const [doValidateRoutingNumber] = useValidateRoutingNumberLazyQuery({
    onCompleted: (data) => {
      const currentInstitutionNumber = institutionNumberField.value ?? "";
      const currentTransitNumber = transitNumberField.value ?? "";
      const currentRoutingNumber = buildCaCpaRoutingNumber(
        currentInstitutionNumber,
        currentTransitNumber,
      );
      const responseRoutingNumber = data?.validateRoutingNumber?.routingNumber ?? "";

      if (
        normalizeRoutingNumber(currentRoutingNumber) !==
        normalizeRoutingNumber(responseRoutingNumber)
      ) {
        return;
      }

      const { bankName, bankAddress, isValid } = data?.validateRoutingNumber || {};
      handleValidateCompleted({ setShowBankValidatorError, bankName, bankAddress, isValid });
    },
    context: clientContext(),
  });

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  const debouncedValidate = useDebouncedFn(doValidateRoutingNumber, 500);

  useEffect(() => {
    const institutionNumber = institutionNumberField.value ?? "";
    const transitNumber = transitNumberField.value ?? "";

    const isValid = EftBankDetailsInputSchema.pick([
      BankDetailsInputFields.InstitutionNumber,
      BankDetailsInputFields.TransitNumber,
    ]).isValidSync({
      [BankDetailsInputFields.InstitutionNumber]: institutionNumber,
      [BankDetailsInputFields.TransitNumber]: transitNumber,
    });

    if (isValid) {
      debouncedValidate({
        variables: {
          validateRoutingNumberInput: {
            routingNumber: buildCaCpaRoutingNumber(institutionNumber, transitNumber),
            routingNumberType: PaymentsRoutingNumberType.CaCpa,
          },
        },
      });
    } else {
      handleClear();
      setShowBankValidatorError(false);
    }
  }, [institutionNumberField.value, transitNumberField.value, debouncedValidate, handleClear]);

  return (
    <FieldGroup title="Bank details">
      <SimpleFormRow>
        <BankCountryReadOnly />
      </SimpleFormRow>
      <MultiInputFormRow>
        <FormTextInput
          isFullWidth
          name={BankDetailsInputFields.InstitutionNumber}
          label={
            <HelpIndicator
              id="institution-number-help-indicator"
              placement="right"
              data-cy="help-icon"
              content="A 3-digit code that identifies your financial institution. It can be found on your bank statement or cheque."
            >
              <Text>
                Institution number{" "}
                <Icon type="question-circle" size="small" variant="outline" />
              </Text>
            </HelpIndicator>
          }
        />
        <FormTextInput label="Bank name" isFullWidth name={BankDetailsInputFields.BankName} />
      </MultiInputFormRow>
      <SimpleFormRow>
        <Flex flexDirection="column">
          <FormTextInput
            isFullWidth
            name={BankDetailsInputFields.TransitNumber}
            label={
              <HelpIndicator
                id="transit-number-help-indicator"
                placement="right"
                data-cy="help-icon"
                content="A 5-digit number that identifies your specific bank branch. It can be found on your bank statement or cheque."
              >
                <Text>
                  Transit number <Icon type="question-circle" size="small" variant="outline" />
                </Text>
              </HelpIndicator>
            }
          />
          {showBankValidatorError && <ErrorMessage>Unrecognized routing code</ErrorMessage>}
        </Flex>
      </SimpleFormRow>
      <BankCountryMismatchWarning
        bankCountryName={selectedBankCountry?.name}
        validatedCountryField={BankDetailsInputFields.BankCountryValidatedCountry}
      />
    </FieldGroup>
  );
};
